/**
 * Face detection utilities for AppBouncer SDK
 * Consolidates shared calculations from age.js and liveness.js
 *
 * NOTE: Liveness thresholds are now centralized in liveness-config.ts.
 * The DEFAULT_CONFIG here is for face quality checks (positioning, scale, etc.)
 * Brightness thresholds are imported from liveness-config for consistency.
 */

import { DEFAULT_THRESHOLDS as LIVENESS_THRESHOLDS } from '../liveness/config';
import { FACE_MESSAGES_DETAILED } from '../utils/face-quality-messages';

// ============================================================
// Types
// ============================================================

export interface Landmark {
  x: number;
  y: number;
  z?: number;
}

export interface HeadPose {
  pitch: number; // Vertical rotation (looking up/down)
  yaw: number; // Horizontal rotation (looking left/right)
  roll: number; // Tilt (head tilting sideways)
}

export interface FaceBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

export interface FaceQualityCheck {
  ok: boolean;
  reason?: string;
  scaleType?: 'too_small' | 'too_large';
}

export interface SceneQualityResult {
  issues: string[];
  brightness: number;
  isValid: boolean;
  // UI-friendly boolean flags
  faceDetected: boolean;
  faceInCenter: boolean;
  faceTooSmall: boolean;
  faceTooLarge: boolean;
  lightingOk: boolean;
  faceInsideOval: boolean;
}

export interface LightingQuality {
  ok: boolean;
  reason?: string;
  mean: number;
  variance: number;
}

// ============================================================
// Landmark Indices (MediaPipe Face Mesh 468 landmarks)
// ============================================================

/** MediaPipe landmark indices for left eye */
export const LEFT_EYE_INDICES = [33, 160, 158, 133, 153, 144] as const;

/** MediaPipe landmark indices for right eye */
export const RIGHT_EYE_INDICES = [362, 385, 387, 263, 373, 380] as const;

/** MediaPipe landmark indices for mouth (outer) */
export const MOUTH_INDICES = [61, 291, 81, 311, 13, 14] as const;

/** Key facial feature indices */
export const FEATURE_INDICES = {
  noseTip: 1,
  leftEyeOuter: 33,
  rightEyeOuter: 263,
  leftEyeInner: 133,
  rightEyeInner: 362,
  chin: 199,
  leftEar: 234,
  rightEar: 454,
  mouthLeft: 61,
  mouthRight: 291,
  upperLip: 13,
  lowerLip: 14,
  leftBrow: 70,
  rightBrow: 300,
  leftBrowEye: 63,
  rightBrowEye: 296,
  leftEyeTop: 159,
  leftEyeBottom: 145,
  rightEyeTop: 386,
  // Additional brow landmarks for better detection
  leftBrowCenter: 105, // Center of left eyebrow (moves more when raising)
  rightBrowCenter: 334, // Center of right eyebrow
  leftBrowOuter: 46, // Outer left eyebrow
  rightBrowOuter: 276, // Outer right eyebrow
} as const;

// ============================================================
// Thresholds & Configuration
// ============================================================

export interface FaceDetectionConfig {
  /** Eye Aspect Ratio threshold for blink detection (default: 0.19) */
  blinkEarThreshold: number;
  /** Minimum frames below EAR threshold to count as blink (default: 2) */
  blinkMinFrames: number;
  /** Yaw angle threshold for "turned left" detection (default: -18) */
  yawLeftDeg: number;
  /** Yaw angle threshold for "turned right" detection (default: 18) */
  yawRightDeg: number;
  /** Roll angle threshold for head tilt (default: 12) */
  rollThreshold: number;
  /** Pitch upper threshold for looking down (default: 30) */
  pitchUpperThreshold: number;
  /** Pitch lower threshold for looking up (default: -30) */
  pitchLowerThreshold: number;
  /** Minimum face width ratio for "too far" detection (default: 0.25) */
  minFaceWidth: number;
  /** Maximum face width ratio for "too close" detection (default: 0.60) */
  maxFaceWidth: number;
  /** Minimum face height ratio (default: 0.30) */
  minFaceHeight: number;
  /** Maximum face height ratio (default: 0.75) */
  maxFaceHeight: number;
  /** Minimum brightness threshold (default: 60) */
  minBrightness: number;
  /** Maximum brightness threshold (default: 220) */
  maxBrightness: number;
  /** Display container width in px (for object-cover oval alignment) */
  containerWidth?: number;
  /** Display container height in px (for object-cover oval alignment) */
  containerHeight?: number;
}

export const DEFAULT_CONFIG: FaceDetectionConfig = {
  blinkEarThreshold: 0.19,
  blinkMinFrames: 2,
  yawLeftDeg: -18,
  yawRightDeg: 18,
  rollThreshold: 12,
  pitchUpperThreshold: 30,
  pitchLowerThreshold: -30,
  minFaceWidth: 0.25,
  maxFaceWidth: 0.6,
  minFaceHeight: 0.3,
  maxFaceHeight: 0.75,
  // Brightness thresholds aligned with liveness-config.ts
  minBrightness: LIVENESS_THRESHOLDS.minBrightness,
  maxBrightness: LIVENESS_THRESHOLDS.maxBrightness,
};

// ============================================================
// CSS Oval Constants (must match styles.css .face-oval)
// ============================================================

/** Oval dimensions as percentages of the container (must match .face-oval in input.css: 85% x 88%) */
const CSS_OVAL_WIDTH_RATIO = 0.85;
const CSS_OVAL_HEIGHT_RATIO = 0.88;

// ============================================================
// Container-Aware Computations
// ============================================================

/**
 * Resolve the display container dimensions.
 *
 * The video element used for detection may be hidden (`camera-feed`), so
 * `video.clientWidth/clientHeight` would equal the native video resolution
 * rather than the visible container.  When explicit `containerWidth/Height`
 * are provided (via config), use those; otherwise fall back to the video
 * element's layout size with a sanity check.
 */
function resolveContainerSize(
  video: HTMLVideoElement,
  containerWidth?: number,
  containerHeight?: number
): { cw: number; ch: number } | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;

  if (containerWidth && containerHeight) {
    return { cw: containerWidth, ch: containerHeight };
  }

  const cw = video.clientWidth;
  const ch = video.clientHeight;

  // If video has no CSS sizing (hidden element), clientWidth equals videoWidth
  // — which means we cannot derive the container mapping.
  if (!cw || !ch || !vw || !vh || (cw === vw && ch === vh)) {
    return null;
  }

  return { cw, ch };
}

/**
 * Compute oval ellipse radii in normalized video coordinates that match
 * the visual CSS oval, accounting for `object-cover` scaling and cropping.
 *
 * Without this, the mathematical oval (0.3, 0.4) diverges from the visual
 * oval on devices where the camera aspect ratio differs from the container.
 */
export function computeOvalRadii(
  video: HTMLVideoElement,
  containerWidth?: number,
  containerHeight?: number
): { radiusX: number; radiusY: number } {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const dims = resolveContainerSize(video, containerWidth, containerHeight);

  if (!vw || !vh || !dims) {
    return { radiusX: 0.35, radiusY: 0.35 };
  }

  const { cw, ch } = dims;

  // object-cover: scale video to fully COVER the container, then center-crop
  const scale = Math.max(cw / vw, ch / vh);

  // Oval is a percentage of the container, compute actual pixel size
  const ovalW = CSS_OVAL_WIDTH_RATIO * cw;
  const ovalH = CSS_OVAL_HEIGHT_RATIO * ch;

  return {
    radiusX: ovalW / 2 / (scale * vw),
    radiusY: ovalH / 2 / (scale * vh),
  };
}

/**
 * Compute face scale thresholds in normalized video coordinates that
 * correspond to meaningful visual proportions of the CSS oval.
 *
 * Face should be 35–105% of the oval width and 35–95% of the oval height
 * to be considered properly positioned.
 */
export function computeFaceScaleForContainer(
  video: HTMLVideoElement,
  containerWidth?: number,
  containerHeight?: number
): Partial<FaceDetectionConfig> | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const dims = resolveContainerSize(video, containerWidth, containerHeight);

  if (!vw || !vh || !dims) {
    return null; // Use DEFAULT_CONFIG fallback
  }

  const { cw, ch } = dims;
  const ovalW = CSS_OVAL_WIDTH_RATIO * cw;
  const ovalH = CSS_OVAL_HEIGHT_RATIO * ch;

  const scale = Math.max(cw / vw, ch / vh);

  return {
    minFaceWidth: (ovalW * 0.65) / (scale * vw),
    maxFaceWidth: (ovalW * 1.05) / (scale * vw),
    minFaceHeight: (ovalH * 0.5) / (scale * vh),
    maxFaceHeight: (ovalH * 0.95) / (scale * vh),
  };
}

// ============================================================
// Basic Math Utilities
// ============================================================

/**
 * Calculate Euclidean distance between two landmarks
 */
export function distance(a: Landmark, b: Landmark): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = (a.z || 0) - (b.z || 0);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate 2D Euclidean distance (ignoring z)
 */
export function distance2D(a: Landmark, b: Landmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Calculate average of an array of numbers
 */
export function average(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((sum, x) => sum + x, 0) / arr.length;
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// ============================================================
// Face Bounds & Positioning
// ============================================================

/**
 * Calculate bounding box from landmarks
 */
export function computeFaceBounds(landmarks: Landmark[]): FaceBounds {
  const xs = landmarks.map((p) => p.x);
  const ys = landmarks.map((p) => p.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Check if face center is within an elliptical mask region
 * @param landmarks - Face landmarks (normalized 0-1)
 * @param ellipseRadiusX - Ellipse radius X as fraction of canvas width (default: 0.30)
 * @param ellipseRadiusY - Ellipse radius Y as fraction of canvas height (default: 0.40)
 */
export function isFaceInsideOval(
  landmarks: Landmark[],
  ellipseRadiusX = 0.3,
  ellipseRadiusY = 0.4
): boolean {
  const bounds = computeFaceBounds(landmarks);

  // Check if face center is within ellipse (centered at 0.5, 0.5)
  const dx = (bounds.centerX - 0.5) / ellipseRadiusX;
  const dy = (bounds.centerY - 0.5) / ellipseRadiusY;

  return dx * dx + dy * dy <= 1;
}

// ============================================================
// Eye Aspect Ratio (EAR) - Blink Detection
// ============================================================

/**
 * Compute Eye Aspect Ratio for a single eye
 * EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
 * Lower EAR indicates closed eye
 */
export function computeEyeEAR(landmarks: Landmark[], eyeIndices: readonly number[]): number {
  const p = (idx: number): Landmark => landmarks[eyeIndices[idx]];

  if (!p(0) || !p(1) || !p(2) || !p(3) || !p(4) || !p(5)) {
    return 1.0; // Default to "open" if landmarks missing
  }

  const verticalA = distance2D(p(1), p(5)); // p2 to p6
  const verticalB = distance2D(p(2), p(4)); // p3 to p5
  const horizontal = distance2D(p(0), p(3)); // p1 to p4

  if (horizontal === 0) return 1.0;

  return (verticalA + verticalB) / (2 * horizontal);
}

/**
 * Compute average Eye Aspect Ratio for both eyes
 */
export function computeEAR(landmarks: Landmark[]): number {
  const leftEAR = computeEyeEAR(landmarks, LEFT_EYE_INDICES);
  const rightEAR = computeEyeEAR(landmarks, RIGHT_EYE_INDICES);
  return (leftEAR + rightEAR) / 2;
}

/**
 * Detect if eyes are currently closed based on EAR
 */
export function areEyesClosed(
  landmarks: Landmark[],
  threshold: number = DEFAULT_CONFIG.blinkEarThreshold
): boolean {
  return computeEAR(landmarks) < threshold;
}

// ============================================================
// Mouth Aspect Ratio (MAR) - Smile/Open Mouth Detection
// ============================================================

/**
 * Compute Mouth Aspect Ratio
 * Higher MAR indicates open mouth
 */
export function computeMAR(landmarks: Landmark[]): number {
  const upperLip = landmarks[FEATURE_INDICES.upperLip];
  const lowerLip = landmarks[FEATURE_INDICES.lowerLip];
  const mouthLeft = landmarks[FEATURE_INDICES.mouthLeft];
  const mouthRight = landmarks[FEATURE_INDICES.mouthRight];

  if (!upperLip || !lowerLip || !mouthLeft || !mouthRight) {
    return 0;
  }

  const verticalDist = distance2D(upperLip, lowerLip);
  const horizontalDist = distance2D(mouthLeft, mouthRight);

  if (horizontalDist === 0) return 0;

  return verticalDist / horizontalDist;
}

/**
 * Compute smile metric based on mouth width relative to face width
 */
export function computeSmileMetric(landmarks: Landmark[]): number {
  const mouthLeft = landmarks[FEATURE_INDICES.mouthLeft];
  const mouthRight = landmarks[FEATURE_INDICES.mouthRight];
  const leftEar = landmarks[FEATURE_INDICES.leftEar];
  const rightEar = landmarks[FEATURE_INDICES.rightEar];

  if (!mouthLeft || !mouthRight || !leftEar || !rightEar) {
    return 0;
  }

  const mouthWidth = distance2D(mouthLeft, mouthRight);
  const faceWidth = distance2D(leftEar, rightEar);

  if (faceWidth === 0) return 0;

  return mouthWidth / faceWidth;
}

// ============================================================
// Head Pose Estimation (Yaw, Pitch, Roll)
// ============================================================

/**
 * Estimate yaw angle (horizontal rotation) from landmarks
 * Positive yaw = looking right, Negative yaw = looking left
 * Returns angle in degrees (approximate)
 *
 * Uses eye-nose ratio method which is more stable across different
 * face distances and shapes compared to nose-to-center offset.
 *
 * NOTE: The video is displayed MIRRORED (selfie view), so landmarks are mirrored.
 * We negate the result so that the yaw matches the user's PHYSICAL direction:
 * - User physically turns LEFT  → negative yaw (passes LOOK_LEFT)
 * - User physically turns RIGHT → positive yaw (passes LOOK_RIGHT)
 */
export function estimateYaw(landmarks: Landmark[]): number {
  const leftEye = landmarks[FEATURE_INDICES.leftEyeOuter]; // Index 33
  const rightEye = landmarks[FEATURE_INDICES.rightEyeOuter]; // Index 263
  const nose = landmarks[FEATURE_INDICES.noseTip]; // Index 1

  if (!leftEye || !rightEye || !nose) return 0;

  // Distance from nose to each eye (X axis only)
  const leftDist = nose.x - leftEye.x; // Distance from left eye to nose
  const rightDist = rightEye.x - nose.x; // Distance from nose to right eye

  // Avoid division by zero
  const totalDist = leftDist + rightDist;
  if (totalDist < 0.001) return 0;

  // Ratio-based yaw: 0 = centered
  // In mirrored video: turning left physically makes ratio positive (head appears to turn right)
  // We negate to correct for mirroring so physical direction matches expected yaw
  const ratio = (leftDist - rightDist) / totalDist;

  // Scale to approximate degrees (calibrated for typical face proportions)
  // A ratio of ~0.3 corresponds to roughly 15-20 degrees of turn
  // Negate to correct for mirrored selfie video
  return -(ratio * 50);
}

/**
 * Alternative yaw estimation using eye positions
 * More robust for some face orientations
 */
export function estimateYawFromEyes(landmarks: Landmark[]): number {
  const leftEye = landmarks[FEATURE_INDICES.leftEyeOuter];
  const rightEye = landmarks[FEATURE_INDICES.rightEyeOuter];
  const nose = landmarks[FEATURE_INDICES.noseTip];

  if (!leftEye || !rightEye || !nose) return 0;

  // Normalized x differences
  return -(rightEye.x - leftEye.x) * 100;
}

/**
 * Estimate pitch angle (vertical rotation) from landmarks
 * Positive pitch = looking down, Negative pitch = looking up
 * Returns angle in degrees (approximate)
 */
export function estimatePitch(landmarks: Landmark[]): number {
  const nose = landmarks[FEATURE_INDICES.noseTip];
  const chin = landmarks[FEATURE_INDICES.chin];

  if (!nose || !chin) return 0;

  // Chin-to-nose vertical distance as proxy for pitch
  return (chin.y - nose.y) * 150;
}

/**
 * Alternative pitch estimation using mouth position
 */
export function estimatePitchFromMouth(landmarks: Landmark[]): number {
  const nose = landmarks[FEATURE_INDICES.noseTip];
  const mouthLeft = landmarks[FEATURE_INDICES.mouthLeft];

  if (!nose || !mouthLeft) return 0;

  return (mouthLeft.y - nose.y) * 100;
}

/**
 * Estimate roll angle (head tilt) from landmarks
 * Positive roll = tilted right, Negative roll = tilted left
 * Returns angle in degrees
 */
export function estimateRoll(landmarks: Landmark[]): number {
  const leftEye = landmarks[FEATURE_INDICES.leftEyeOuter];
  const rightEye = landmarks[FEATURE_INDICES.rightEyeOuter];

  if (!leftEye || !rightEye) return 0;

  const dx = rightEye.x - leftEye.x;
  const dy = rightEye.y - leftEye.y;

  return Math.atan2(dy, dx) * (180 / Math.PI);
}

/**
 * Get complete head pose estimation
 */
export function estimateHeadPose(landmarks: Landmark[]): HeadPose {
  return {
    pitch: estimatePitch(landmarks),
    yaw: estimateYaw(landmarks),
    roll: estimateRoll(landmarks),
  };
}

// ============================================================
// Eyebrow Detection
// ============================================================

/**
 * Compute eyebrow raise metric using multiple brow landmarks for more robust detection.
 * Uses center brow points (105, 334) which move more dramatically when raising eyebrows,
 * compared against stable eye top landmarks (159, 386).
 * Returns the average vertical distance from brow to eye landmarks.
 * In MediaPipe coordinates, Y=0 is top, so when brows are raised:
 * - brow.y decreases (moves up)
 * - the returned value becomes more negative
 */
export function computeBrowRaise(landmarks: Landmark[]): number {
  // Primary: center brow points (most sensitive to raising)
  const leftBrowCenter = landmarks[FEATURE_INDICES.leftBrowCenter];
  const rightBrowCenter = landmarks[FEATURE_INDICES.rightBrowCenter];

  // Reference: upper eyelid points (stable reference)
  const leftEyeTop = landmarks[FEATURE_INDICES.leftEyeTop];
  const rightEyeTop = landmarks[FEATURE_INDICES.rightEyeTop];

  // Fallback to original landmarks if center brow not available
  const leftBrow = leftBrowCenter || landmarks[FEATURE_INDICES.leftBrow];
  const rightBrow = rightBrowCenter || landmarks[FEATURE_INDICES.rightBrow];
  const leftEye = leftEyeTop || landmarks[FEATURE_INDICES.leftBrowEye];
  const rightEye = rightEyeTop || landmarks[FEATURE_INDICES.rightBrowEye];

  if (!leftBrow || !leftEye || !rightBrow || !rightEye) {
    return 0;
  }

  // Calculate vertical distance (brow Y - eye Y)
  // Negative when brows are above eyes (normal)
  // More negative when brows are raised higher
  const leftDist = leftBrow.y - leftEye.y;
  const rightDist = rightBrow.y - rightEye.y;

  return (leftDist + rightDist) / 2;
}

// ============================================================
// Face Quality Checks
// ============================================================

/**
 * Check if face is at appropriate scale (not too far/close)
 */
export function checkFaceScale(
  landmarks: Landmark[],
  config: Partial<FaceDetectionConfig> = {}
): FaceQualityCheck {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const bounds = computeFaceBounds(landmarks);

  if (bounds.width < cfg.minFaceWidth || bounds.height < cfg.minFaceHeight) {
    return { ok: false, reason: FACE_MESSAGES_DETAILED.TOO_FAR, scaleType: 'too_small' };
  }

  if (bounds.width > cfg.maxFaceWidth || bounds.height > cfg.maxFaceHeight) {
    return { ok: false, reason: FACE_MESSAGES_DETAILED.TOO_CLOSE, scaleType: 'too_large' };
  }

  return { ok: true };
}

/**
 * Check if head angles are within acceptable range
 */
export function checkHeadAngles(
  landmarks: Landmark[],
  config: Partial<FaceDetectionConfig> = {}
): FaceQualityCheck {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const pose = estimateHeadPose(landmarks);

  if (Math.abs(pose.roll) > cfg.rollThreshold) {
    return { ok: false, reason: 'Keep your head straight (tilting sideways)' };
  }

  if (pose.pitch > cfg.pitchUpperThreshold) {
    return { ok: false, reason: 'Lower your chin slightly' };
  }

  if (pose.pitch < cfg.pitchLowerThreshold) {
    return { ok: false, reason: 'Raise your chin slightly' };
  }

  return { ok: true };
}

/**
 * Check face centering in frame
 */
export function checkFaceCentering(
  landmarks: Landmark[],
  toleranceX = 0.2,
  toleranceY = 0.2
): FaceQualityCheck {
  const bounds = computeFaceBounds(landmarks);
  const issues: string[] = [];

  if (bounds.centerX < 0.5 - toleranceX) {
    issues.push('Move face to the right');
  } else if (bounds.centerX > 0.5 + toleranceX) {
    issues.push('Move face to the left');
  }

  if (bounds.centerY < 0.5 - toleranceY) {
    issues.push('Move face down');
  } else if (bounds.centerY > 0.5 + toleranceY) {
    issues.push('Move face up');
  }

  if (issues.length > 0) {
    return { ok: false, reason: issues[0] };
  }

  return { ok: true };
}

/**
 * Check ear visibility (both ears should be roughly visible)
 */
export function checkEarVisibility(landmarks: Landmark[]): FaceQualityCheck {
  const leftEar = landmarks[FEATURE_INDICES.leftEar];
  const rightEar = landmarks[FEATURE_INDICES.rightEar];

  if (!leftEar || !rightEar) {
    return { ok: false, reason: 'Ears not fully visible' };
  }

  const earDist = Math.abs(leftEar.x - rightEar.x);
  if (earDist < 0.15) {
    return { ok: false, reason: 'Turn head so both ears are visible' };
  }

  return { ok: true };
}

/**
 * Check yaw angle for straight-on face detection
 */
export function checkYawStraight(landmarks: Landmark[], tolerance = 12): FaceQualityCheck {
  const yaw = estimateYaw(landmarks);

  if (yaw < -tolerance) {
    return { ok: false, reason: 'Turn head slightly right' };
  }

  if (yaw > tolerance) {
    return { ok: false, reason: 'Turn head slightly left' };
  }

  return { ok: true };
}

// ============================================================
// Image Quality Analysis
// ============================================================

/**
 * Analyze brightness from video frame
 * Returns average brightness (0-255)
 */
export function analyzeBrightness(video: HTMLVideoElement, sampleSize = 32): number {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return -1;
  }

  const canvas = document.createElement('canvas');
  canvas.width = sampleSize;
  canvas.height = sampleSize;

  const ctx = canvas.getContext('2d');
  if (!ctx) return -1;

  ctx.drawImage(video, 0, 0, sampleSize, sampleSize);
  const data = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    total += data[i] + data[i + 1] + data[i + 2];
  }

  return total / (sampleSize * sampleSize * 3);
}

/**
 * Analyze lighting quality in face region
 */
export function analyzeLightingQuality(
  video: HTMLVideoElement,
  landmarks: Landmark[]
): LightingQuality {
  const bounds = computeFaceBounds(landmarks);

  const w = video.videoWidth;
  const h = video.videoHeight;

  if (w === 0 || h === 0) {
    return { ok: false, reason: 'Video not ready', mean: 0, variance: 0 };
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { ok: false, reason: 'Canvas context unavailable', mean: 0, variance: 0 };
  }

  ctx.drawImage(video, 0, 0);

  // Extract face region
  const sx = Math.max(0, Math.floor(bounds.minX * w));
  const sy = Math.max(0, Math.floor(bounds.minY * h));
  let sw = Math.max(1, Math.floor(bounds.width * w));
  let sh = Math.max(1, Math.floor(bounds.height * h));

  if (sx + sw > w) sw = w - sx;
  if (sy + sh > h) sh = h - sy;

  if (sw <= 0 || sh <= 0) {
    return { ok: false, reason: 'Invalid face region', mean: 0, variance: 0 };
  }

  const imageData = ctx.getImageData(sx, sy, sw, sh);
  const data = imageData.data;

  // Calculate luminance mean
  let mean = 0;
  const pixelCount = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const lum = data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722;
    mean += lum;
  }
  mean /= pixelCount;

  // Calculate variance
  let variance = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722;
    const diff = lum - mean;
    variance += diff * diff;
  }
  variance /= pixelCount;

  // Check thresholds (using centralized config)
  if (variance < LIVENESS_THRESHOLDS.minVariance) {
    return { ok: false, reason: 'Lighting is too weak or flat', mean, variance };
  }

  if (mean > LIVENESS_THRESHOLDS.maxBrightness) {
    return { ok: false, reason: 'Face is overexposed, reduce brightness', mean, variance };
  }

  return { ok: true, mean, variance };
}

/**
 * Detect potential glasses/occlusion in eye region
 */
export function detectGlasses(video: HTMLVideoElement, landmarks: Landmark[]): FaceQualityCheck {
  const leftEyeTop = landmarks[FEATURE_INDICES.leftEyeTop];
  const leftEyeBottom = landmarks[FEATURE_INDICES.leftEyeBottom];
  const leftEyeOuter = landmarks[FEATURE_INDICES.leftEyeOuter];
  const leftEyeInner = landmarks[FEATURE_INDICES.leftEyeInner];

  if (!leftEyeTop || !leftEyeBottom || !leftEyeOuter || !leftEyeInner) {
    return { ok: true }; // Can't detect, assume ok
  }

  const w = video.videoWidth;
  const h = video.videoHeight;

  if (w === 0 || h === 0) {
    return { ok: true };
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) return { ok: true };

  ctx.drawImage(video, 0, 0);

  // Extract eye region
  const minX = Math.min(leftEyeOuter.x, leftEyeInner.x) * w;
  const maxX = Math.max(leftEyeOuter.x, leftEyeInner.x) * w;
  const minY = Math.min(leftEyeTop.y, leftEyeBottom.y) * h;
  const maxY = Math.max(leftEyeTop.y, leftEyeBottom.y) * h;

  let sx = Math.max(0, Math.floor(minX));
  let sy = Math.max(0, Math.floor(minY));
  let sw = Math.max(1, Math.floor(maxX - minX));
  let sh = Math.max(1, Math.floor(maxY - minY));

  if (sx + sw > w) sw = w - sx;
  if (sy + sh > h) sh = h - sy;

  if (sw <= 0 || sh <= 0) {
    return { ok: true };
  }

  const imageData = ctx.getImageData(sx, sy, sw, sh);
  const data = imageData.data;

  // Check average brightness
  let brightnessSum = 0;
  for (let i = 0; i < data.length; i += 4) {
    brightnessSum += (data[i] + data[i + 1] + data[i + 2]) / 3;
  }
  const avgBrightness = brightnessSum / (data.length / 4);

  if (avgBrightness < 40) {
    return { ok: false, reason: 'Remove dark glasses or reduce reflections' };
  }

  return { ok: true };
}

// ============================================================
// Comprehensive Scene Quality Check
// ============================================================

/**
 * Run all scene quality checks
 */
export function checkSceneQuality(
  video: HTMLVideoElement,
  landmarks: Landmark[],
  config: Partial<FaceDetectionConfig> = {},
  ovalRadiusMultiplier = 1.0
): SceneQualityResult {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const issues: string[] = [];

  // Track individual checks for UI flags
  let lightingOk = true;
  let faceInCenter = true;
  let faceTooSmall = false;
  let faceTooLarge = false;

  // Check if we have landmarks (face detected)
  const faceDetected = landmarks && landmarks.length > 0;

  if (!faceDetected) {
    issues.push('No face detected');
    return {
      issues,
      brightness: 0,
      isValid: false,
      faceDetected: false,
      faceInCenter: false,
      faceTooSmall: false,
      faceTooLarge: false,
      lightingOk: true,
      faceInsideOval: false,
    };
  }

  // Compute container-aware thresholds (accounts for object-cover crop)
  const containerScales = computeFaceScaleForContainer(
    video,
    cfg.containerWidth,
    cfg.containerHeight
  );
  if (containerScales) {
    if (containerScales.minFaceWidth != null) cfg.minFaceWidth = containerScales.minFaceWidth;
    if (containerScales.maxFaceWidth != null) cfg.maxFaceWidth = containerScales.maxFaceWidth;
    if (containerScales.minFaceHeight != null) cfg.minFaceHeight = containerScales.minFaceHeight;
    if (containerScales.maxFaceHeight != null) cfg.maxFaceHeight = containerScales.maxFaceHeight;
  }

  // Brightness check
  const brightness = analyzeBrightness(video);
  if (brightness >= 0) {
    if (brightness < cfg.minBrightness) {
      issues.push('Lighting is too dark');
      lightingOk = false;
    }
    if (brightness > cfg.maxBrightness) {
      issues.push('Lighting is too bright');
      lightingOk = false;
    }
  }

  // Face centering
  const centerCheck = checkFaceCentering(landmarks);
  if (!centerCheck.ok) {
    faceInCenter = false;
    if (centerCheck.reason) {
      issues.push(centerCheck.reason);
    }
  }

  // Face scale (uses container-aware thresholds)
  const scaleCheck = checkFaceScale(landmarks, cfg);
  if (!scaleCheck.ok) {
    if (scaleCheck.reason) {
      issues.push(scaleCheck.reason);
      // Determine if too small or too large based on the scale type
      if (scaleCheck.scaleType === 'too_small') {
        faceTooSmall = true;
      } else if (scaleCheck.scaleType === 'too_large') {
        faceTooLarge = true;
      }
    }
  }

  // Yaw angle
  const yawCheck = checkYawStraight(landmarks);
  if (!yawCheck.ok && yawCheck.reason) {
    issues.push(yawCheck.reason);
  }

  // Ear visibility
  const earCheck = checkEarVisibility(landmarks);
  if (!earCheck.ok && earCheck.reason) {
    issues.push(earCheck.reason);
  }

  // Oval check — use container-aware radii that match the visual CSS oval
  const ovalRadii = computeOvalRadii(video, cfg.containerWidth, cfg.containerHeight);
  const faceInsideOval = isFaceInsideOval(
    landmarks,
    ovalRadii.radiusX * ovalRadiusMultiplier,
    ovalRadii.radiusY * ovalRadiusMultiplier
  );
  if (!faceInsideOval) {
    issues.push('Face outside oval guide');
  }

  return {
    issues,
    brightness,
    isValid: issues.length === 0,
    faceDetected,
    faceInCenter,
    faceTooSmall,
    faceTooLarge,
    lightingOk,
    faceInsideOval,
  };
}

// ============================================================
// Landmark Mirroring (for selfie camera)
// ============================================================

/**
 * Mirror landmarks horizontally (for selfie/front camera)
 */
export function mirrorLandmarks(landmarks: Landmark[]): Landmark[] {
  return landmarks.map((p) => ({
    ...p,
    x: 1 - p.x,
  }));
}

// ============================================================
// Sleep Utility
// ============================================================

/**
 * Promise-based sleep utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
